import React, { ReactNode } from "react";
import {
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Text,
} from "@chakra-ui/react";
import CustomIcon from "../../../../../../public/icons-v1_1";

interface Props {
  title: string;
  isExpanded: boolean;
  // 제목 오른쪽 배지 — 설정 목록은 완료 여부를, FAQ는 아무것도 넘기지 않는다.
  badge?: ReactNode;
  // 접혔을 때 제목 아래 한 줄로 보여줄 요약. 요약이 없는 행은 생략한다.
  summary?: string;
  // 펼쳤을 때 본문 — 설명 전문과 액션 버튼, 또는 인라인 폼.
  children: ReactNode;
}

/**
 * 시작 가이드의 아코디언 행 — 설정 목록과 FAQ가 함께 쓴다.
 *
 * 접힘/펼침 상태 관리와 aria-expanded·aria-controls·키보드 조작은 Chakra Accordion 이 맡는다.
 * 단일/다중 열림 여부는 감싸는 `<Accordion>` 이 정한다.
 */
function PathPickStartGuideAccordionRow({
  title,
  isExpanded,
  badge,
  summary,
  children,
}: Props) {
  return (
    <AccordionItem
      border={"none"}
      borderBottom={"0.5px solid #EDEEF0"}
      _last={{ borderBottom: "none" }}
      // 펼친 행만 흰색으로 떠오르고 접힌 행은 한 톤 가라앉는다.
      backgroundColor={isExpanded ? "#FFFFFF" : "#F8F9FB"}
    >
      <AccordionButton
        padding={"14px 20px"}
        alignItems={"flex-start"}
        gap={"10px"}
        textAlign={"left"}
        _hover={{ backgroundColor: isExpanded ? "#FFFFFF" : "#F2F4F7" }}
      >
        <Flex
          alignItems={"center"}
          justifyContent={"center"}
          flexShrink={0}
          width={"24px"}
          height={"24px"}
          borderRadius={"6px"}
          backgroundColor={"#E8F2FF"}
          color={"#1272EF"}
          // 펼치면 아래를 가리켜 열린 상태를 방향으로도 알린다.
          transform={isExpanded ? "rotate(90deg)" : undefined}
          transition={"transform 0.15s ease"}
        >
          <CustomIcon src={"chevron-right"} width={16} height={16} />
        </Flex>
        <Flex flexDirection={"column"} gap={"4px"} minWidth={0} flexGrow={1}>
          <Flex gap={"8px"} alignItems={"center"} flexWrap={"wrap"}>
            <Text textStyle={"body-sm-bold"} color={"#22221F"}>
              {title}
            </Text>
            {badge}
          </Flex>
          {/* 접혔을 때만 요약을 한 줄로 보여준다 — 펼치면 본문에 전문이 나온다. */}
          {summary && !isExpanded && (
            <Text
              textStyle={"body-sm-medium"}
              color={"#6F6F6C"}
              noOfLines={1}
              wordBreak={"break-all"}
            >
              {summary}
            </Text>
          )}
        </Flex>
      </AccordionButton>
      <AccordionPanel padding={"0 20px 16px 54px"}>
        <Box>{children}</Box>
      </AccordionPanel>
    </AccordionItem>
  );
}

export default PathPickStartGuideAccordionRow;
